// src/pages/Admin/Users/EditUserModal.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaCloudUploadAlt,
  FaLink,
  FaImage,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateUser, getUserById } from "../../../services/userServices";
import { toast } from "react-toastify";

// ⚠️ services photo USER (mêmes signatures que adhérents)
import {
  uploadUserPhoto,
  cropUserPhoto,
  toAbsoluteUserPhotoUrl,
} from "../../../services/userPhotoServices";

import CropPhotoModal, {
  toMasterUrl,
} from "../../../components/CropPhotoModal/CropPhotoModal";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

// Ajoute un cache-bust pour forcer le refresh de l'<img>
const withBust = (u, v) =>
  !u ? u : u.includes("?") ? `${u}&v=${v}` : `${u}?v=${v}`;

export default function EditUserModal({
  isOpen,
  onClose,
  refreshList,
  userId,
}) {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    role_id: "",
    photoURL: "",
  });
  const [saving, setSaving] = useState(false);

  // —— Gestion upload/crop (copie du pattern Adhérent) ——
  const [imgOk, setImgOk] = useState(true);
  const [uploadMode, setUploadMode] = useState("url"); // 'url' | 'upload'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const fileInputRef = useRef(null);
  const [imgVersion, setImgVersion] = useState(0); // anti-cache preview
  const [isCropOpen, setIsCropOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !userId) return;
    (async () => {
      try {
        const data = await getUserById(userId);
        setFormData({
          nom: data?.nom || "",
          prenom: data?.prenom || "",
          email: data?.email || "",
          role_id: data?.role_id ?? "",
          photoURL: data?.photoURL || "",
        });
        setImgOk(true);
        setUploadMode("url");
        setUploadProgress(0);
        setIsUploading(false);
        setImgVersion((v) => v + 1);
      } catch (err) {
        console.error("Erreur lors du chargement de l'utilisateur:", err);
        toast.error("Erreur lors du chargement des données de l'utilisateur");
      }
    })();
  }, [isOpen, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "role_id" ? parseInt(value || "0", 10) : value,
    }));
    // Si l'URL de la photo est modifiée manuellement, on force le refresh
    if (name === "photoURL") {
      setImgOk(true);
      setImgVersion((v) => v + 1);
    }
  };

  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!userId) {
      toast.error("ID utilisateur manquant.");
      return;
    }
    try {
      setIsUploading(true);
      setUploadProgress(0);

      const res = await uploadUserPhoto(userId, file, {
        format: outputFormat,
        onUploadProgress: (pe) => {
          if (!pe.total) return;
          const percent = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percent);
        },
      });

      // 🔧 Rendre l’extraction robuste (selon ce que renvoie l’API)
      const newThumbUrl =
        res?.photo_url ||
        res?.photoURL ||
        res?.imageUrl ||
        res?.url ||
        res?.path ||
        res?.data?.photo_url ||
        res?.data?.photoURL ||
        res?.data?.imageUrl ||
        res?.data?.url ||
        res?.data?.path;

      if (newThumbUrl) {
        setFormData((prev) => ({ ...prev, photoURL: newThumbUrl }));
        setImgOk(true);
        setImgVersion((v) => v + 1); // force refresh miniature + modale crop
        toast.success("Photo uploadée et traitée (400×400).");
      } else {
        // Log pour debug si nécessaire
        console.warn("Réponse upload inattendue:", res);
        toast.warn("Upload terminé mais URL non renvoyée.");
      }

      // Permettre de re-sélectionner le même fichier
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      toast.error(`Erreur upload: ${err?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUploading) {
      toast.info("Patiente, l'upload est en cours…");
      return;
    }
    try {
      setSaving(true);
      await updateUser(userId, { ...formData });
      refreshList?.();
      onClose?.();
      toast.success("Utilisateur mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'utilisateur", error);
      toast.error("Erreur lors de la mise à jour de l'utilisateur");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const absolutePhoto = toAbsoluteUserPhotoUrl(formData.photoURL);
  const previewSrc = formData.photoURL
    ? withBust(absolutePhoto, imgVersion)
    : withBust(DEFAULT_AVATAR, imgVersion);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full max-w-4xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Éditer un utilisateur
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Mettez à jour les informations de ce compte.
          </p>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-5 sm:p-6 space-y-6 max-h-[75vh] overflow-y-auto">
            {/* Bloc identité & avatar */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <div className="px-4 py-3 border-b border-slate-200/70 dark:border-slate-800/70">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Identité & avatar
                </h4>
              </div>

              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Infos texte */}
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Nom
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Prénom
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Rôle
                    </label>
                    <select
                      name="role_id"
                      value={formData.role_id}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    >
                      <option value="1">Administrateur</option>
                      <option value="2">Utilisateur</option>
                      <option value="3">Enseignant</option>
                    </select>
                  </div>
                </div>

                {/* Photo : URL / Upload + Aperçu (pattern identique adhérent) */}
                <div
                  className="md:col-span-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="block text-sm font-medium mb-2">
                    Photo utilisateur
                  </label>

                  <div className="flex items-center gap-4 mb-2">
                    <label
                      className="inline-flex items-center gap-2 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="radio"
                        name="photoMode"
                        value="url"
                        checked={uploadMode === "url"}
                        onChange={() => setUploadMode("url")}
                      />
                      <span className="flex items-center gap-2 text-sm">
                        <FaLink /> Lien URL
                      </span>
                    </label>
                    <label
                      className="inline-flex items-center gap-2 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="radio"
                        name="photoMode"
                        value="upload"
                        checked={uploadMode === "upload"}
                        onChange={() => setUploadMode("upload")}
                      />
                      <span className="flex items-center gap-2 text-sm">
                        <FaCloudUploadAlt /> Uploader
                      </span>
                    </label>
                  </div>

                  {uploadMode === "url" ? (
                    <input
                      type="text"
                      name="photoURL"
                      value={formData.photoURL}
                      onChange={handleChange}
                      placeholder="https://… ou /uploads/…"
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div
                      className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/70 dark:bg-slate-900/30"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {/* INPUT FILE CACHÉ */}
                      <input
                        ref={fileInputRef}
                        id="user-photo-file"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />

                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className=" bg-primary rounded-xl gap-2 px-3 py-1.5 text-sm text-white hover:bg-primary-dark flex items-center hover:bg-white/90 hover:text-gray-800 transition ring-1 ring-primary/70 hover:ring-primary"
                            onClick={openFilePicker}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            Choisir un fichier
                          </button>

                          <div className="flex items-center gap-2">
                            <span className="text-sm">Format</span>
                            <select
                              className="select select-bordered select-sm"
                              value={outputFormat}
                              onChange={(e) => setOutputFormat(e.target.value)}
                              disabled={isUploading}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="webp">WEBP (recommandé)</option>
                              <option value="jpg">JPG</option>
                              <option value="png">PNG</option>
                            </select>
                          </div>
                        </div>

                        {isUploading && (
                          <div>
                            <progress
                              className="progress w-full"
                              value={uploadProgress}
                              max="100"
                            ></progress>
                            <div className="text-xs mt-1">
                              {uploadProgress}%
                            </div>
                          </div>
                        )}

                        <p className="text-xs opacity-70">
                          L’image sera traitée côté serveur en carré 400×400 et
                          optimisée.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Aperçu */}
                  <div className="mt-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FaImage />
                      <span>
                        Aperçu
                        {uploadMode === "upload"
                          ? " (cliquer pour choisir un fichier)"
                          : ""}
                      </span>
                    </div>

                    <div
                      className="mt-2 h-28 w-28 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (uploadMode === "upload") openFilePicker(e);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <img
                        key={imgVersion} // force remount (anti-cache)
                        src={previewSrc}
                        alt="Aperçu"
                        onError={() => setImgOk(false)}
                        className="h-full w-full object-cover pointer-events-none"
                      />
                    </div>

                    {/* Recadrage si visuel présent */}
                    {formData.photoURL && (
                      <button
                        type="button"
                        className="mt-2 h-12 w-28 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCropOpen(true);
                        }}
                      >
                        Recadrer / Zoom
                      </button>
                    )}
                    <p className="text-xs text-slate-500">
                      Le recadrage de la photo est possible de suite après
                      l'upload d'un fichier.
                    </p>

                    {!imgOk && (
                      <div className="mt-2 text-xs text-rose-600">
                        Impossible de charger l’image.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div
            className="px-5 pb-5 sm:px-6 sm:pb-6 flex justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={saving ? "Enregistrement…" : "Mettre à jour"}
              icon={FaSave}
              type="submit"
              className="!rounded-xl gap-2"
              disabled={isUploading || saving}
            />
          </div>
        </form>
      </div>

      {/* Modale de recadrage (exactement comme adhérent) */}
      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={userId}
        imageUrl={
          formData.photoURL
            ? `${toAbsoluteUserPhotoUrl(
                toMasterUrl(formData.photoURL)
              )}?v=${imgVersion}`
            : ""
        }
        onCropped={(newUrl) => {
          // le backend renvoie un chemin /uploads/...-400.webp
          setFormData((p) => ({ ...p, photoURL: newUrl }));
          setImgVersion((v) => v + 1); // anti-cache pour miniature & crop
        }}
        format="webp"
        cropAction={cropUserPhoto}
      />
    </div>
  );
}
